import { FC, useState } from 'react';
import {
  ProgramTagWithCountDataFragment,
  PatientNameDataFragment,
} from '../../../generated/graphql';
import classNames from 'classnames';
import { getTagStyles, DEFAULT_TAG_COLOR } from '../../lib/colors';
import {
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/outline';
import { pluralize } from '../../lib/copy';
import Tag from '../../svgs/Tag';
import TooltipLight from '../../components/TooltipLight';
import Checkbox from '../../components/Checkbox';

const ProgramTagPill: FC<{
  programTag: ProgramTagWithCountDataFragment;
  patients: Array<{
    patient: PatientNameDataFragment;
    selected: boolean;
  }>;
  onDelete?: (programTag: ProgramTagWithCountDataFragment) => void;
  onTogglePatient?: (
    programTag: ProgramTagWithCountDataFragment,
    patient: PatientNameDataFragment,
    selected: boolean,
  ) => void;
  className?: string;
}> = ({ programTag, patients, onDelete, onTogglePatient, className }) => {
  const selectedCount = patients.filter(({ selected }) => selected).length;
  const totalCount = patients.length;
  const allSelected = selectedCount === totalCount;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const tooltipContent = (
    <div className="flex flex-col gap-2 p-2">
      {patients.map(({ patient, selected }) => (
        <Checkbox
          key={`${programTag.id}-${patient.programInstanceId}`}
          id={`${programTag.id}-${patient.programInstanceId}`}
          checked={selected}
          onChange={(event) =>
            onTogglePatient?.(programTag, patient, event.target.checked)
          }
          labelContent={patient.name}
        />
      ))}
      {!patients.length && (
        <div className="text-caption text-neutral-150">No clients selected</div>
      )}
    </div>
  );

  const tagStyles = getTagStyles(programTag.color ?? DEFAULT_TAG_COLOR);

  return (
    <li
      key={programTag.id}
      className={classNames(
        'relative flex flex-row items-center gap-x-2 rounded-full py-2 px-4',
        className,
      )}
      style={tagStyles}
    >
      <div className="flex flex-col items-start">
        {/* First line: Tag icon and name */}
        <div className="flex flex-row items-center gap-x-1.5">
          <Tag className="h-4 w-4" />
          <span className="font-medium">{programTag.name}</span>
        </div>

        {/* Second line: Dropdown trigger */}
        <TooltipLight
          placement="bottom-start"
          content={tooltipContent}
          enabled
          className="inline-block"
          interactive
          onVisibilityChange={setIsTooltipOpen}
        >
          <div className="text-neutral-600 hover:text-neutral-900 flex cursor-pointer flex-row items-center gap-x-1 text-caption">
            {isTooltipOpen ? (
              <ChevronDownIcon className="h-3 w-3" />
            ) : (
              <ChevronRightIcon className="h-3 w-3" />
            )}
            <span>
              {allSelected
                ? `All ${pluralize(totalCount, 'client', 'clients')}`
                : `${selectedCount} of ${pluralize(
                    totalCount,
                    'client',
                    'clients',
                  )}`}
            </span>
          </div>
        </TooltipLight>
      </div>

      {onDelete && (
        <div
          onClick={() => {
            onDelete(programTag);
          }}
          className="ml-2"
        >
          <TrashIcon
            className={
              'text-neutral-600 h-5 w-5 cursor-pointer rounded-full bg-white/50 p-1 hover:bg-red-100 hover:text-white'
            }
          />
        </div>
      )}
    </li>
  );
};

export default ProgramTagPill;
